import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLang } from '../context/LangContext';
import api from '../utils/api';
import './AIConfig.css';

const BUSINESS_TYPES = [
  'restaurant', 'ecommerce', 'services', 'retail', 'salon',
  'clinic', 'gym', 'real_estate', 'education', 'other'
];

const BUSINESS_TYPE_LABELS = {
  en: { restaurant: 'Restaurant', ecommerce: 'E-commerce', services: 'Services', retail: 'Retail', salon: 'Salon / Spa', clinic: 'Clinic', gym: 'Gym / Fitness', real_estate: 'Real Estate', education: 'Education', other: 'Other' },
  es: { restaurant: 'Restaurante', ecommerce: 'E-commerce', services: 'Servicios', retail: 'Tienda', salon: 'Salón / Spa', clinic: 'Clínica', gym: 'Gym / Fitness', real_estate: 'Bienes Raíces', education: 'Educación', other: 'Otro' },
};

const PERSONALITIES = [
  { key: 'friendly', icon: '😊', en: 'Friendly', es: 'Amigable' },
  { key: 'professional', icon: '👔', en: 'Professional', es: 'Profesional' },
  { key: 'direct', icon: '🎯', en: 'Direct', es: 'Directo' },
  { key: 'casual', icon: '😎', en: 'Casual', es: 'Casual' },
];

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = {
  en: { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' },
  es: { mon: 'Lunes', tue: 'Martes', wed: 'Miércoles', thu: 'Jueves', fri: 'Viernes', sat: 'Sábado', sun: 'Domingo' },
};

const defaultHours = () => {
  const h = {};
  DAYS.forEach(d => { h[d] = { open: '09:00', close: '18:00', closed: d === 'sun' }; });
  return h;
};

export default function AIConfig() {
  const { lang } = useLang();
  const location = useLocation();
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [instances, setInstances] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  /** Auto-generated prompt + live appointments block (matches WhatsApp runtime; not persisted in DB). */
  const [promptPreview, setPromptPreview] = useState('');

  // Business Profile
  const [bp, setBp] = useState({
    business_name: '', business_type: '', business_description: '',
    business_address: '', business_phone: '', business_website: '',
    currency: 'USD', business_hours: defaultHours(),
  });

  // AI Config
  const [ai, setAi] = useState({
    instance_id: '', ai_name: '', personality: 'friendly',
    response_language: 'auto', response_length: 'medium',
    intelligence_level: 'standard', auto_reply: true, custom_prompt: '',
  });

  useEffect(() => {
    // Load data
    api.get('/rt/business-profile').then(r => {
      if (r.data?.data) {
        const d = r.data.data;
        let hours = defaultHours();
        try { hours = typeof d.business_hours === 'string' ? JSON.parse(d.business_hours) : d.business_hours || defaultHours(); } catch { /* keep defaultHours */ }
        setBp({
          business_name: d.business_name || '',
          business_type: d.business_type || '',
          business_description: d.business_description || '',
          business_address: d.business_address || '',
          business_phone: d.business_phone || '',
          business_website: d.business_website || '',
          currency: d.currency || 'USD',
          business_hours: hours,
        });
      }
    });

    api.get('/qr/get_all').then(r => {
      if (r.data?.success) setInstances(r.data.data || []);
    });
  }, []);

  useEffect(() => {
    if (location.pathname !== '/app/ai-config') return;
    api.get('/rt/ai-config').then(r => {
      if (r.data?.data) {
        const d = r.data.data;
        setAi({
          instance_id: d.instance_id || '',
          ai_name: d.ai_name || '',
          personality: d.personality || 'friendly',
          response_language: d.response_language || 'auto',
          response_length: d.response_length || 'medium',
          intelligence_level: d.intelligence_level || 'standard',
          auto_reply: d.auto_reply === 1,
          custom_prompt: d.custom_prompt || '',
        });
        setPromptPreview(d.preview_prompt || d.generated_prompt || '');
      }
    });
  }, [location.pathname]);

  const updateBp = (key, val) => setBp(prev => ({ ...prev, [key]: val }));
  const updateAi = (key, val) => setAi(prev => ({ ...prev, [key]: val }));
  const updateHour = (day, field, val) => {
    setBp(prev => ({
      ...prev,
      business_hours: { ...prev.business_hours, [day]: { ...prev.business_hours[day], [field]: val } }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      await api.post('/rt/business-profile', bp);
      const res = await api.post('/rt/ai-config', ai);
      if (res.data?.preview_prompt != null && res.data.preview_prompt !== '') {
        setPromptPreview(res.data.preview_prompt);
      } else if (res.data?.generated_prompt) {
        setPromptPreview(res.data.generated_prompt);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="aiconfig">
      <div className="aiconfig__header">
        <div>
          <h1>{lang === 'en' ? 'AI Configuration' : 'Configuración de IA'}</h1>
          <p>{lang === 'en' ? 'Set up your AI assistant with clicks — no coding needed' : 'Configura tu asistente IA con clics — sin programación'}</p>
        </div>
        <button type="button" className="aiconfig__save" onClick={handleSave} disabled={saving}>
          {saving ? '...' : saved ? (lang === 'en' ? '✓ Saved!' : '✓ Guardado!') : (lang === 'en' ? 'Save Configuration' : 'Guardar Configuración')}
        </button>
      </div>

      {/* ── SECTION A: BUSINESS PROFILE ── */}
      <div className="aiconfig__section">
        <h2>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }}>
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          {lang === 'en' ? 'Business Profile' : 'Perfil del Negocio'}
        </h2>

        <div className="aiconfig__grid">
          <div className="aiconfig__field">
            <label htmlFor="aiconfig-biz-name">{lang === 'en' ? 'Business Name' : 'Nombre del Negocio'}</label>
            <input id="aiconfig-biz-name" value={bp.business_name || ''} onChange={e => updateBp('business_name', e.target.value)}
              placeholder={lang === 'en' ? 'e.g. Bella Nails Studio' : 'ej. Bella Nails Studio'} />
          </div>
          <div className="aiconfig__field">
            <label htmlFor="aiconfig-biz-type">{lang === 'en' ? 'Business Type' : 'Tipo de Negocio'}</label>
            <select id="aiconfig-biz-type" value={bp.business_type || ''} onChange={e => updateBp('business_type', e.target.value)}>
              <option value="">{lang === 'en' ? 'Select...' : 'Seleccionar...'}</option>
              {BUSINESS_TYPES.map(t => (
                <option key={t} value={t}>{BUSINESS_TYPE_LABELS[lang][t]}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="aiconfig__field">
          <label htmlFor="aiconfig-biz-desc">{lang === 'en' ? 'Business Description' : 'Descripción del Negocio'}</label>
          <textarea id="aiconfig-biz-desc" rows={3} value={bp.business_description || ''} onChange={e => updateBp('business_description', e.target.value)}
            placeholder={lang === 'en' ? 'Tell us about your business — the AI will use this to answer customers' : 'Cuéntanos sobre tu negocio — la IA usará esto para responder clientes'} />
        </div>

        <div className="aiconfig__grid">
          <div className="aiconfig__field">
            <label htmlFor="aiconfig-address">{lang === 'en' ? 'Address (optional)' : 'Dirección (opcional)'}</label>
            <input id="aiconfig-address" value={bp.business_address || ''} onChange={e => updateBp('business_address', e.target.value)}
              placeholder={lang === 'en' ? 'e.g. 123 Main St, Miami' : 'ej. Calle 123, Ciudad'} />
          </div>
          <div className="aiconfig__field">
            <label htmlFor="aiconfig-currency">{lang === 'en' ? 'Currency' : 'Moneda'}</label>
            <select id="aiconfig-currency" value={bp.currency || 'USD'} onChange={e => updateBp('currency', e.target.value)}>
              <option value="USD">USD ($)</option>
              <option value="EUR">EUR (€)</option>
              <option value="MXN">MXN ($)</option>
              <option value="COP">COP ($)</option>
              <option value="ARS">ARS ($)</option>
              <option value="BRL">BRL (R$)</option>
              <option value="CLP">CLP ($)</option>
              <option value="PEN">PEN (S/)</option>
              <option value="DOP">DOP (RD$)</option>
            </select>
          </div>
        </div>

        <div className="aiconfig__grid">
          <div className="aiconfig__field">
            <label>{lang === 'en' ? 'Business Phone' : 'Teléfono del Negocio'}</label>
            <input value={bp.business_phone || ''} onChange={e => updateBp('business_phone', e.target.value)}
              placeholder={lang === 'en' ? 'e.g. +1 305 555 1234' : 'ej. +1 305 555 1234'} />
          </div>
          <div className="aiconfig__field">
            <label>{lang === 'en' ? 'Website (optional)' : 'Página Web (opcional)'}</label>
            <input value={bp.business_website || ''} onChange={e => updateBp('business_website', e.target.value)}
              placeholder="https://www.example.com" />
          </div>
        </div>

        {/* Business Hours */}
        <div className="aiconfig__field">
          <span className="aiconfig__field-label-text">{lang === 'en' ? 'Business Hours' : 'Horario del Negocio'}</span>
          <div className="aiconfig__hours">
            {DAYS.map(day => (
              <div className="hour-row" key={day}>
                <span className="hour-row__day">{DAY_LABELS[lang][day]}</span>
                <label className="hour-row__toggle">
                  <input type="checkbox" checked={!bp.business_hours?.[day]?.closed}
                    onChange={e => updateHour(day, 'closed', !e.target.checked)} />
                  <span>{bp.business_hours?.[day]?.closed ? (lang === 'en' ? 'Closed' : 'Cerrado') : (lang === 'en' ? 'Open' : 'Abierto')}</span>
                </label>
                {!bp.business_hours?.[day]?.closed && (
                  <>
                    <input type="time" className="hour-row__time" value={bp.business_hours?.[day]?.open || '09:00'}
                      onChange={e => updateHour(day, 'open', e.target.value)} />
                    <span className="hour-row__sep">—</span>
                    <input type="time" className="hour-row__time" value={bp.business_hours?.[day]?.close || '18:00'}
                      onChange={e => updateHour(day, 'close', e.target.value)} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── SECTION B: AI PERSONALITY ── */}
      <div className="aiconfig__section">
        <h2>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--accent-cyan)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.5rem' }}>
            <rect x="3" y="11" width="18" height="10" rx="2" />
            <circle cx="12" cy="5" r="3" />
            <line x1="12" y1="8" x2="12" y2="11" />
            <circle cx="8" cy="16" r="1" />
            <circle cx="16" cy="16" r="1" />
          </svg>
          {lang === 'en' ? 'AI Personality' : 'Personalidad de la IA'}
        </h2>

        <div className="aiconfig__grid">
          <div className="aiconfig__field">
            <label htmlFor="aiconfig-ai-name">{lang === 'en' ? 'AI Name' : 'Nombre de la IA'}</label>
            <input id="aiconfig-ai-name" value={ai.ai_name} onChange={e => updateAi('ai_name', e.target.value)}
              placeholder={lang === 'en' ? 'e.g. Luna, Max, Sofia' : 'ej. Luna, Max, Sofía'} />
          </div>
          <div className="aiconfig__field">
            <label htmlFor="aiconfig-instance">{lang === 'en' ? 'WhatsApp Profile' : 'Perfil WhatsApp'}</label>
            <select id="aiconfig-instance" value={ai.instance_id} onChange={e => updateAi('instance_id', e.target.value)}>
              <option value="">{lang === 'en' ? 'Select profile...' : 'Seleccionar perfil...'}</option>
              {instances.filter(i => i.status === 'ACTIVE').map(inst => (
                <option key={inst.uniqueId} value={inst.uniqueId}>
                  {inst.number ? `+${String(inst.number).replace(/^\+/, '')}` : inst.title}
                  {inst.title && inst.number ? ` (${inst.title})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Personality Cards */}
        <div className="aiconfig__field">
          <span className="aiconfig__field-label-text">{lang === 'en' ? 'Personality Style' : 'Estilo de Personalidad'}</span>
          <div className="personality-cards" role="group" aria-label={lang === 'en' ? 'Personality' : 'Personalidad'}>
            {PERSONALITIES.map(p => (
              <button
                type="button"
                key={p.key}
                className={`personality-card ${ai.personality === p.key ? 'personality-card--active' : ''}`}
                onClick={() => updateAi('personality', p.key)}
              >
                <span className="personality-card__icon">{p.icon}</span>
                <span>{p[lang]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="aiconfig__grid aiconfig__grid--3">
          <div className="aiconfig__field">
            <label htmlFor="aiconfig-lang">{lang === 'en' ? 'Response Language' : 'Idioma de Respuestas'}</label>
            <select id="aiconfig-lang" value={ai.response_language} onChange={e => updateAi('response_language', e.target.value)}>
              <option value="auto">{lang === 'en' ? 'Auto-detect' : 'Auto-detectar'}</option>
              <option value="spanish">{lang === 'en' ? 'Spanish' : 'Español'}</option>
              <option value="english">{lang === 'en' ? 'English' : 'Inglés'}</option>
              <option value="portuguese">{lang === 'en' ? 'Portuguese' : 'Portugués'}</option>
            </select>
          </div>
          <div className="aiconfig__field">
            <span className="aiconfig__field-label-text">{lang === 'en' ? 'Response Length' : 'Longitud de Respuesta'}</span>
            <div className="radio-group">
              {['short', 'medium', 'long'].map(v => (
                <label key={v} className={`radio-pill ${ai.response_length === v ? 'radio-pill--active' : ''}`}>
                  <input type="radio" name="length" value={v} checked={ai.response_length === v}
                    onChange={() => updateAi('response_length', v)} />
                  {v === 'short' ? (lang === 'en' ? 'Short' : 'Corta') : v === 'medium' ? (lang === 'en' ? 'Medium' : 'Media') : (lang === 'en' ? 'Long' : 'Larga')}
                </label>
              ))}
            </div>
          </div>
          <div className="aiconfig__field">
            <div className="toggle-container">
              <span className="toggle-label">{lang === 'en' ? 'Auto-Reply' : 'Auto-Respuesta'}</span>
              <button
                type="button"
                className={`toggle-btn ${ai.auto_reply ? 'toggle-btn--on' : 'toggle-btn--off'}`}
                onClick={() => updateAi('auto_reply', !ai.auto_reply)}
              >
                <span className="toggle-btn__knob" />
                <span className="toggle-btn__text">{ai.auto_reply ? 'ON' : 'OFF'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── SECTION C: ADVANCED ── */}
      <div className="aiconfig__section">
        <button type="button" className="aiconfig__advanced-toggle" onClick={() => setShowAdvanced(!showAdvanced)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.4rem' }}>
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
          {lang === 'en' ? 'Advanced Settings' : 'Configuración Avanzada'}
          <span>{showAdvanced ? '▲' : '▼'}</span>
        </button>

        {showAdvanced && (
          <div className="aiconfig__advanced">
            <div className="aiconfig__field">
              <label htmlFor="aiconfig-custom-prompt">{lang === 'en' ? 'Custom System Prompt (overrides auto-generated)' : 'Prompt personalizado (sobreescribe el auto-generado)'}</label>
              <textarea id="aiconfig-custom-prompt" rows={6} value={ai.custom_prompt} onChange={e => updateAi('custom_prompt', e.target.value)}
                placeholder={lang === 'en' ? 'Leave empty to use auto-generated prompt...' : 'Deja vacío para usar el prompt auto-generado...'} />
            </div>
            {promptPreview && (
              <div className="aiconfig__field">
                <span className="aiconfig__field-label-text">{lang === 'en' ? 'Auto-Generated Prompt (preview)' : 'Prompt Auto-Generado (vista previa)'}</span>
                <div className="aiconfig__prompt-preview">{promptPreview}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
